<template>
    <div class="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div class="bg-white shadow-lg rounded-sm mb-8">
            <div class="flex flex-col md:flex-row md:-mr-px">
                <!-- Panel -->
                <div class="grow">
                    <!-- Panel body -->
                    <div class="p-6 space-y-6">
                        <h2 class="text-2xl text-gray-800 font-bold mb-5">Mon Profil</h2>
                        <!-- Picture -->
                        <section class="relative">
                            <input type="file" ref="picture" style="display:none" @change="handlePicture" />
                            <PictureAvatarUser :user="$page.props.user" :currentPicture="currentPicture" />
                            <div @click="$refs.picture.click()" style="top:5.5rem" class="absolute z-1 bg-white left-16 text-center
                                justify-center items-center flex rounded-full shadow p-4 w-6 h-6 border border-gray-200"
                            >
                                <i v-if="form.photo" class="fad fa-pencil text-xs"></i>
                                <i v-else class="fad fa-image cursor-pointer text-xs"></i>
                            </div>
                        </section>
                        <!-- Business Profile -->
                        <section>
                            <h3 class="text-xl leading-snug text-gray-800 font-bold mb-1">Profil Business</h3>
                            <div class="text-sm">Votre nom et numéro de téléphone sont très importants. Il faut donc se donner la peine de les renseigner correctement.</div>
                            <div class="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                                <div class="sm:w-1/2">
                                    <jet-label for="name" value="Nom & prénom(s):" />
                                    <jet-input id="name" type="text" v-model="form.name" maxlength="80"
                                        placeholder="Entrer nom & prénom(s)" autocomplete="off"
                                    />
                                    <jet-input-error :message="form.errors.name" />
                                </div>
                                <div class="sm:w-1/2">
                                    <jet-label for="phone" value="Numéro de téléphone:" />
                                    <vue-tel-input required @input="phoneInput"
                                        :defaultCountry="form.country_code" :value="form.phone"
                                    />
                                    <jet-input-error :message="form.errors.phone" />
                                </div>
                            </div>
                        </section>

                        <hr class="my-2">
                        <!-- Email -->
                        <section>
                            <h3 class="text-xl leading-snug text-gray-800 font-bold mb-1">Adresse mail</h3>
                            <div class="text-sm">Votre adresse mail sera utilisée pour vous tenir au courant des informations concernant Boti Cargo Shipping.</div>
                            <div class="mt-5">
                                <jet-label for="email" value="Adresse mail:" />
                                <jet-input id="email" type="email" v-model="form.email"
                                    placeholder="Entrer votre adresse mail" autocomplete="off"
                                />
                                <jet-input-error :message="form.errors.email" />
                            </div>
                        </section>

                        <hr class="my-2">
                        <!-- Password -->
                        <section>
                            <h3 class="text-xl leading-snug text-gray-800 font-bold mb-1">Mot de passe</h3>
                            <div class="text-sm">Vous pouvez définir un nouveau mot de passe si vous ne souhaitez plus utiliser celui que vous avez actuellement.</div>
                            <div class="mt-5">
                                <button @click="showPasswordInfos()" class="btn border-red-200 shadow-sm text-red-500 hover:text-white hover:bg-red-500">
                                    Modifier mon mot de passe
                                </button>
                            </div>
                            <transition
                                enter-active-class="transition ease-out duration-200"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95"
                            >
                                <div v-if="passwordInfos" class="flex flex-wrap mt-5">
                                    <div class="w-full lg:w-1/3 px-1">
                                        <jet-label  class="block text-sm font-medium mb-1" for="current_password" value="Ancien mot de passe:" />
                                        <jet-input id="current_password" type="password" v-model="form.current_password"
                                            placeholder="Entrer ancien mot de passe" autocomplete="off"
                                        />
                                        <jet-input-error :message="form.errors.current_password" />
                                    </div>
                                    <div class="w-full lg:w-1/3 px-1">
                                        <jet-label class="block text-sm font-medium mb-1" for="password" value="Nouveau mot de passe:" />
                                        <jet-input id="password" type="password" v-model="form.password"
                                            placeholder="Entrer nouveau mot de passe" autocomplete="off"
                                        />
                                        <jet-input-error :message="form.errors.password" />
                                    </div>
                                    <div class="w-full lg:w-1/3 px-1">
                                        <jet-label  class="block text-sm font-medium mb-1" for="password_confirmation" value="Confimer le nouveau mot de passe:" />
                                        <jet-input id="password_confirmation" type="password" v-model="form.password_confirmation"
                                            placeholder="Répéter le nouveau mot de passe" autocomplete="off"
                                        />
                                        <jet-input-error :message="form.errors.password_confirmation" />
                                    </div>
                                </div>
                            </transition>
                        </section>
                    </div>
                    <!-- Panel footer -->
                    <footer>
                        <div class="flex flex-col px-6 py-5 border-t border-gray-200">
                            <div class="flex self-end">
                                <boti-button :loader="form.processing" :disabled="form.processing"
                                    @click="updateProfilInfos" class="w-full" color="sky"
                                    :class="{ 'opacity-25': form.processing }" text="Mettre à jour"
                                >
                                    <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                                    </svg>
                                    <template #icon>
                                        <i class="fad fa-check-circle text-xs ml-1"></i>
                                    </template>
                                </boti-button>
                            </div>
                        </div>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PicturesActionsMixins from '@/Mixins/PicturesActionsMixins'
    import PictureAvatarUser from '@/Components/PictureAvatarUser'
    import JetLabel from '@/Jetstream/Label'
    import RebuildPhoneMixins from '@/Mixins/RebuildPhoneMixins'
    import JetInput from '@/Jetstream/Input'
    import BotiButton from '@/Components/Button'
    import JetInputError from '@/Jetstream/InputError'

    export default {

        props: ['linkUpdadeProfile'],

        components: {
            PictureAvatarUser,
            JetInput,
            JetLabel,
            BotiButton,
            JetInputError
        },

        mixins: [RebuildPhoneMixins, PicturesActionsMixins],

        data() {
            return {
                passwordInfos: false,

                form: this.$inertia.form({
                    photo: null,
                    name: this.$page.props.user.name,
                    email: this.$page.props.user.email,
                    phone: this.$page.props.user.phone,
                    dial_code: this.$page.props.user.dial_code,
                    country_code: this.$page.props.user.country_code,
                    current_password: '',
                    password: '',
                    password_confirmation: '',
                })
            }
        },
       methods: {
        updateProfilInfos() {
            this.form.post(route(this.linkUpdadeProfile), {
                preserveScroll: true,
                onSuccess: () =>{
                    this.$swal.fire(
                        'Profil',
                        'Votre profil à été mis à jour avec succès.',
                        'success'
                    )
                },
                onError: (error) =>{
                    console.log(error)
                }
            })
        },

        showPasswordInfos() {
            this.passwordInfos = !this.passwordInfos
        }
       }

    }
</script>
